import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useQuery } from '@apollo/client';
import dayjs from 'dayjs';
import { SERVER_URL } from '../../../../databucket';
import { selectAllMatching } from '../../../../Slices/bulkOutreachSlice';
import { Select, DatePicker, Checkbox, Collapse, Input, Spin, Empty, Badge, Switch, message } from 'antd';
import {
  SearchOutlined, FilterOutlined, UserOutlined, MailOutlined,
  CheckCircleFilled, CloseCircleFilled, WarningFilled,
  SaveOutlined, FolderOpenOutlined, TeamOutlined,
  CaretRightOutlined,
} from '@ant-design/icons';
import { GET_ALL_LEADS, LEADS_FILTERS } from '../../../../Queries/Leads';

const { Panel } = Collapse;
const { RangePicker } = DatePicker;
const { Option } = Select;

const COLORS = {
  primary: '#1a1a2e',
  primaryLight: '#16213e',
  primaryMid: '#0f3460',
  surface: '#1e1e3a',
  surfaceLight: '#252547',
  border: 'rgba(255,255,255,0.08)',
  borderLight: 'rgba(255,255,255,0.12)',
  text: '#e2e8f0',
  textSecondary: '#94a3b8',
  textMuted: '#64748b',
  emerald: '#10b981',
  emeraldBg: 'rgba(16,185,129,0.12)',
  amber: '#f59e0b',
  amberBg: 'rgba(245,158,11,0.12)',
  rose: '#ef4444',
  roseBg: 'rgba(239,68,68,0.12)',
  violet: '#8b5cf6',
  violetBg: 'rgba(139,92,246,0.12)',
  blue: '#3b82f6',
  blueBg: 'rgba(59,130,246,0.12)',
  glass: 'rgba(255,255,255,0.04)',
  glassBorder: 'rgba(255,255,255,0.08)',
};

const glassCard = {
  background: COLORS.glass,
  backdropFilter: 'blur(16px)',
  WebkitBackdropFilter: 'blur(16px)',
  border: `1px solid ${COLORS.glassBorder}`,
  borderRadius: 12,
};

const STATUS_OPTIONS = ['New', 'In Progress', 'Follow Up', 'Contacted', 'Not Interested', 'Closed'];
const STAGE_OPTIONS = ['Research', 'Outreach', 'Engaged', 'Qualified', 'Proposal', 'Negotiation', 'Won', 'Lost'];
const PRIORITY_OPTIONS = ['High', 'Medium', 'Low'];
const PIPELINE_STAGE_OPTIONS = ['Discovery', 'Proposal', 'Negotiation', 'Contract', 'Closed Won', 'Closed Lost'];
const CHANNEL_OPTIONS = ['Cold Call', 'Email', 'LinkedIn', 'Referral', 'Website', 'Other'];

const SUMMARY_CARD_STYLE = {
  ...glassCard,
  padding: '14px 18px',
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  minWidth: 130,
  flex: 1,
};

const DEFAULT_LEAD_FILTERS = {
  status: [], stage: [], state: [], city: [], timeZone: [], channel: [],
  assignedTo: [], priority: [], pipelineStage: [], followup: [],
  successfull: [], callscheduledwithOPs: [], isInPipeline: [],
  isAssignLocked: [], createdBy: [],
  notContactedIn: null,
};
const DEFAULT_DATE_FILTERS = {
  lastUpdated: [null, null],
  lastContacted: [null, null],
  followUp: [null, null],
  created: [null, null],
};
const DEFAULT_CONTACT_FILTERS = {
  title: [], optedIn: [], verified: [], unsubscribed: [],
};

const RecipientSelector = ({ selectedContacts, onContactsChange, salesInfo, initialFilters, onFiltersChange }) => {
  const dispatch = useDispatch();
  const [leadContactStats, setLeadContactStats] = useState(null);
  const [selectingAll, setSelectingAll] = useState(false);
  const [filteredTotalContacts, setFilteredTotalContacts] = useState(null);

  useEffect(() => {
    fetch(`${SERVER_URL}/api/bulk-outreach/lead-contact-stats`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data && data.leads > 0) setLeadContactStats(data); })
      .catch(() => {});
  }, []);

  // Restore date filters from serialized ISO strings → dayjs objects
  const parseSavedDateFilters = (saved) => {
    if (!saved) return { ...DEFAULT_DATE_FILTERS };
    const parsed = {};
    for (const key of Object.keys(DEFAULT_DATE_FILTERS)) {
      const pair = saved[key];
      if (Array.isArray(pair)) {
        parsed[key] = [
          pair[0] ? dayjs(pair[0]) : null,
          pair[1] ? dayjs(pair[1]) : null,
        ];
      } else {
        parsed[key] = [null, null];
      }
    }
    return parsed;
  };

  const [leadFilters, setLeadFilters] = useState(
    initialFilters?.leadFilters || { ...DEFAULT_LEAD_FILTERS }
  );
  const [dateFilters, setDateFilters] = useState(
    () => parseSavedDateFilters(initialFilters?.dateFilters)
  );
  const [contactFilters, setContactFilters] = useState(
    initialFilters?.contactFilters || { ...DEFAULT_CONTACT_FILTERS }
  );
  const [searchText, setSearchText] = useState(initialFilters?.searchText || '');
  const [filtersExpanded, setFiltersExpanded] = useState(
    initialFilters ? true : false
  );
  const [selectedSegment, setSelectedSegment] = useState(null);
  const [segmentName, setSegmentName] = useState('');
  const [expandedLeads, setExpandedLeads] = useState('all'); // 'all' = expand all by default
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);

  const segments = useSelector((state) => state.bulkOutreach?.segments || []);

  // Notify parent of filter changes so they can be persisted with the draft
  // Serialize dayjs dates to ISO strings for backend storage
  useEffect(() => {
    if (onFiltersChange) {
      const serializedDates = {};
      for (const key of Object.keys(dateFilters)) {
        const pair = dateFilters[key];
        serializedDates[key] = [
          pair[0]?.toISOString?.() || null,
          pair[1]?.toISOString?.() || null,
        ];
      }
      onFiltersChange({ leadFilters, dateFilters: serializedDates, contactFilters, searchText });
    }
  }, [leadFilters, dateFilters, contactFilters, searchText]); // eslint-disable-line react-hooks/exhaustive-deps

  const cleanFilters = useMemo(() => {
    const f = {};
    // Array filters — only include if non-empty
    const arrayFields = ['status', 'stage', 'state', 'city', 'timeZone', 'channel', 'priority'];
    arrayFields.forEach(key => {
      if (leadFilters[key]?.length) f[key] = leadFilters[key];
    });
    // Assigned To — handle "Not Assigned" special value
    if (leadFilters.assignedTo?.length) {
      const hasNotAssigned = leadFilters.assignedTo.includes('__not_assigned__');
      const realNames = leadFilters.assignedTo.filter(v => v !== '__not_assigned__');
      if (hasNotAssigned && realNames.length === 0) {
        f.assignedTo = [''];  // empty string matches unassigned leads
      } else if (realNames.length > 0) {
        f.assignedTo = hasNotAssigned ? [...realNames, ''] : realNames;
      }
    }
    // String filters — followup, successfull, callscheduledwithOPs
    if (leadFilters.followup?.length) f.followup = leadFilters.followup[0];
    if (leadFilters.successfull?.length) f.successfull = leadFilters.successfull[0];
    if (leadFilters.callscheduledwithOPs?.length) f.callscheduledwithOPs = leadFilters.callscheduledwithOPs[0];
    // Date range filters
    if (dateFilters.lastUpdated[0]) f.lastUpdatedFrom = dateFilters.lastUpdated[0].startOf('day').toISOString();
    if (dateFilters.lastUpdated[1]) f.lastUpdatedTo = dateFilters.lastUpdated[1].endOf('day').toISOString();
    if (dateFilters.lastContacted[0]) f.lastContactedFrom = dateFilters.lastContacted[0].startOf('day').toISOString();
    if (dateFilters.lastContacted[1]) f.lastContactedTo = dateFilters.lastContacted[1].endOf('day').toISOString();
    if (dateFilters.followUp[0]) f.lastFollowupFrom = dateFilters.followUp[0].startOf('day').toISOString();
    if (dateFilters.followUp[1]) f.lastFollowupTo = dateFilters.followUp[1].endOf('day').toISOString();
    // Not Contacted In X Days — translates to lastContactedTo = X days ago
    if (leadFilters.notContactedIn) {
      if (leadFilters.notContactedIn === 9999) {
        // Never contacted — no lastContactedDate at all (handled server-side with $exists: false)
        f.lastContactedTo = '1970-01-01T00:00:00.000Z'; // very old date = never contacted
      } else {
        const cutoff = new Date(Date.now() - leadFilters.notContactedIn * 24 * 60 * 60 * 1000);
        f.lastContactedTo = cutoff.toISOString();
      }
    }
    // Search
    if (searchText?.trim()) f.search = searchText.trim();
    return f;
  }, [leadFilters, dateFilters, searchText]);

  const { data: filterOptionsData } = useQuery(LEADS_FILTERS, {
    variables: {
      filters: {},
      isPipeline: false,
      leadScope: 'ALL',
      assignedTo: null,
    },
    fetchPolicy: 'cache-first',
  });

  const [leadPage, setLeadPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // Reset page to 1 when filters change + fetch filtered contact count
  useEffect(() => {
    setLeadPage(1);
    let stale = false;
    // Debounced fetch of filtered contact count from server (countOnly for performance)
    const timer = setTimeout(() => {
      dispatch(selectAllMatching({ leadFilters: cleanFilters, contactFilters, countOnly: true }))
        .then(result => {
          // Ignore stale responses from previous filter states (race condition fix)
          if (!stale && result.payload?.count !== undefined) {
            setFilteredTotalContacts(result.payload.count);
          }
        })
        .catch(() => {});
    }, 500);
    return () => { stale = true; clearTimeout(timer); };
  }, [cleanFilters, contactFilters, dispatch]);
  const { data: leadsData, loading: leadsLoading, fetchMore } = useQuery(GET_ALL_LEADS, {
    variables: {
      page: leadPage,
      pageSize,
      filters: cleanFilters,
      sort: {},
      isPipeline: false,
      leadScope: 'ALL',
      assignedTo: null,
    },
    fetchPolicy: 'network-only',
  });

  const filterOptions = filterOptionsData?.getLeadFilters || {};
  const allLeads = useMemo(() => leadsData?.getAllLeads?.response || [], [leadsData]);
  const totalLeadCount = leadsData?.getAllLeads?.count || 0;
  const totalPages = Math.ceil(totalLeadCount / pageSize);

  const filteredLeads = useMemo(() => {
    // Lead-level filters (status, stage, state, city, dates, search, followup,
    // successfull, callscheduledwithOPs) are applied server-side via GraphQL.
    // Only fields NOT supported by GraphQL need client-side filtering:
    let leads = allLeads;

    if (leadFilters.isInPipeline.length) {
      leads = leads.filter((l) => leadFilters.isInPipeline.includes(String(l.isInPipeline)));
    }
    if (leadFilters.isAssignLocked.length) {
      leads = leads.filter((l) => leadFilters.isAssignLocked.includes(String(l.isAssignLocked)));
    }
    if (leadFilters.pipelineStage.length) {
      leads = leads.filter((l) => leadFilters.pipelineStage.includes(l.pipelineStage));
    }
    if (leadFilters.createdBy.length) {
      leads = leads.filter((l) => leadFilters.createdBy.includes(l.createdBy));
    }

    return leads;
  }, [allLeads, leadFilters]);

  const getFilteredContacts = useCallback((contacts) => {
    if (!contacts) return [];
    let filtered = [...contacts];
    if (contactFilters.title.length) {
      filtered = filtered.filter((c) => contactFilters.title.includes(c.title));
    }
    if (contactFilters.optedIn.length) {
      filtered = filtered.filter((c) => contactFilters.optedIn.includes(String(!!c.optIn)));
    }
    if (contactFilters.verified.length) {
      filtered = filtered.filter((c) => contactFilters.verified.includes(c.verifyResult || 'unknown'));
    }
    if (contactFilters.unsubscribed.length) {
      filtered = filtered.filter((c) => contactFilters.unsubscribed.includes(String(!!c.unsubscribedAt)));
    }
    // Deduplicate by email within the same lead — keep the first occurrence
    const seen = new Set();
    filtered = filtered.filter((c) => {
      if (!c.email) return false;
      const key = c.email.toLowerCase().trim();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    return filtered;
  }, [contactFilters]);

  const summaryStats = useMemo(() => {
    // Use server-side stats when available (covers all leads, not just current page)
    const hasServerStats = leadContactStats && leadContactStats.leads > 0;

    if (hasServerStats) {
      return {
        leads: leadContactStats.leads,
        contacts: leadContactStats.contacts,
        optedIn: leadContactStats.optedIn,
        verified: leadContactStats.verified,
        selected: selectedContacts.length,
      };
    }

    // Fallback: compute from loaded leads (current page only)
    let totalContacts = 0;
    let optedIn = 0;
    let verified = 0;
    filteredLeads.forEach((lead) => {
      const contacts = getFilteredContacts(lead.leadsContacts);
      totalContacts += contacts.length;
      contacts.forEach((c) => {
        if (c.optIn) optedIn++;
        if (c.verifyResult === 'valid') verified++;
      });
    });
    return {
      leads: totalLeadCount || filteredLeads.length,
      contacts: totalContacts,
      optedIn,
      verified,
      selected: selectedContacts.length,
    };
  }, [filteredLeads, getFilteredContacts, selectedContacts, leadContactStats, totalLeadCount]);

  const allContactKeys = useMemo(() => {
    const keys = [];
    filteredLeads.forEach((lead) => {
      const contacts = getFilteredContacts(lead.leadsContacts);
      contacts.forEach((c, idx) => {
        if (c.email) keys.push(`${lead.id}__${c.email}__${idx}`);
      });
    });
    return keys;
  }, [filteredLeads, getFilteredContacts]);

  const handleSelectAll = useCallback(() => {
    const contactEntries = [];
    filteredLeads.forEach((lead) => {
      const contacts = getFilteredContacts(lead.leadsContacts);
      contacts.forEach((c, idx) => {
        if (c.email) {
          contactEntries.push({
            key: `${lead.id}__${c.email}__${idx}`,
            leadId: lead.id,
            leadName: lead.agencyName,
            contactPerson: c.contactPerson,
            title: c.title,
            email: c.email,
            optIn: c.optIn,
            verifyResult: c.verifyResult,
          });
        }
      });
    });
    onContactsChange(contactEntries);
  }, [filteredLeads, getFilteredContacts, onContactsChange]);

  const handleDeselectAll = useCallback(() => {
    onContactsChange([]);
  }, [onContactsChange]);

  const handleContactToggle = useCallback((lead, contact, idx) => {
    const key = `${lead.id}__${contact.email}__${idx}`;
    const exists = selectedContacts.find((sc) => sc.key === key);
    if (exists) {
      onContactsChange(selectedContacts.filter((sc) => sc.key !== key));
    } else {
      onContactsChange([
        ...selectedContacts,
        {
          key,
          leadId: lead.id,
          leadName: lead.agencyName,
          contactPerson: contact.contactPerson,
          title: contact.title,
          email: contact.email,
          optIn: contact.optIn,
          verifyResult: contact.verifyResult,
          agencyName: lead.agencyName,
          timeZone: lead.timeZone,
        },
      ]);
    }
  }, [selectedContacts, onContactsChange]);

  const handleLoadSegment = useCallback((segId) => {
    const seg = segments.find((s) => s._id === segId);
    if (seg && seg.filters) {
      try {
        const parsed = typeof seg.filters === 'string' ? JSON.parse(seg.filters) : seg.filters;
        if (parsed.leadFilters) setLeadFilters(parsed.leadFilters);
        if (parsed.contactFilters) setContactFilters(parsed.contactFilters);
        setSelectedSegment(segId);
      } catch (e) {
        console.error('Failed to parse segment filters:', e);
      }
    }
  }, [segments]);

  const multiSelectStyle = {
    width: '100%',
    minHeight: 32,
  };

  const filterLabelStyle = {
    fontSize: 11,
    fontWeight: 600,
    color: COLORS.textSecondary,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: 4,
  };

  const filterGroupStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    flex: 1,
    minWidth: 180,
  };

  const renderSummaryCard = (label, value, icon, color) => (
    <div style={SUMMARY_CARD_STYLE}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ color, fontSize: 18 }}>{icon}</span>
        <span style={{ fontSize: 22, fontWeight: 700, color }}>{value}</span>
      </div>
      <span style={{ fontSize: 11, color: COLORS.textSecondary, fontWeight: 500 }}>{label}</span>
    </div>
  );

  const renderLeadFilterRow = (label, filterKey, options) => (
    <div style={filterGroupStyle}>
      <div style={filterLabelStyle}>{label}</div>
      <Select
        mode="multiple"
        placeholder={`Select ${label}`}
        value={leadFilters[filterKey]}
        onChange={(val) => setLeadFilters((prev) => ({ ...prev, [filterKey]: val }))}
        style={multiSelectStyle}
        maxTagCount={2}
        size="small"
        allowClear
        dropdownStyle={{ background: '#fff' }}
      >
        {(options || []).map((o) => (
          <Option key={o} value={o}>{o}</Option>
        ))}
      </Select>
    </div>
  );

  const renderBooleanFilter = (label, filterKey) => (
    <div style={filterGroupStyle}>
      <div style={filterLabelStyle}>{label}</div>
      <Select
        mode="multiple"
        placeholder={`Select ${label}`}
        value={leadFilters[filterKey]}
        onChange={(val) => setLeadFilters((prev) => ({ ...prev, [filterKey]: val }))}
        style={multiSelectStyle}
        maxTagCount={2}
        size="small"
        allowClear
        dropdownStyle={{ background: '#fff' }}
      >
        <Option value="true">Yes</Option>
        <Option value="false">No</Option>
      </Select>
    </div>
  );

  const renderDateFilter = (label, filterKey) => (
    <div style={filterGroupStyle}>
      <div style={filterLabelStyle}>{label}</div>
      <RangePicker
        size="small"
        style={{ width: '100%' }}
        value={dateFilters[filterKey]}
        onChange={(dates) => setDateFilters((prev) => ({ ...prev, [filterKey]: dates || [null, null] }))}
      />
    </div>
  );

  const allSelected = allContactKeys.length > 0 && allContactKeys.every((k) => selectedContacts.find((sc) => sc.key === k));

  const contactBadge = (label, color, bgColor) => (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px',
      borderRadius: 6, fontSize: 10, fontWeight: 600, color, background: bgColor,
    }}>
      {label}
    </span>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Summary Cards */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {renderSummaryCard('Leads', summaryStats.leads, <TeamOutlined />, COLORS.blue)}
        {renderSummaryCard('Contacts', summaryStats.contacts, <UserOutlined />, COLORS.violet)}
        {renderSummaryCard('Opted In', summaryStats.optedIn, <CheckCircleFilled />, COLORS.emerald)}
        {renderSummaryCard('Verified', summaryStats.verified, <MailOutlined />, COLORS.amber)}
        {renderSummaryCard('Selected', summaryStats.selected, <CheckCircleFilled />, '#3b82f6')}
      </div>

      {/* Segment Selector */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        <Select
          placeholder="Load Segment..."
          value={selectedSegment}
          onChange={handleLoadSegment}
          style={{ flex: 1, maxWidth: 300 }}
          allowClear
          onClear={() => setSelectedSegment(null)}
          suffixIcon={<FolderOpenOutlined style={{ color: COLORS.violet }} />}
          dropdownStyle={{ background: '#fff' }}
        >
          {segments.map((seg) => (
            <Option key={seg._id} value={seg._id}>{seg.name}</Option>
          ))}
        </Select>
        <Input
          placeholder="Save as segment..."
          value={segmentName}
          onChange={(e) => setSegmentName(e.target.value)}
          style={{ maxWidth: 220 }}
          size="middle"
          suffix={
            <SaveOutlined
              style={{ color: segmentName ? COLORS.emerald : COLORS.textMuted, cursor: segmentName ? 'pointer' : 'default' }}
            />
          }
        />
      </div>

      {/* Lead-Level Filters (Collapsible) */}
      <Collapse
        bordered={false}
        activeKey={filtersExpanded ? ['lead-filters'] : []}
        onChange={(keys) => setFiltersExpanded(keys.includes('lead-filters'))}
        expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} style={{ color: COLORS.violet }} />}
        style={{ ...glassCard, padding: 0, overflow: 'hidden' }}
      >
        <Panel
          header={
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <span style={{ color: COLORS.text, fontWeight: 600, fontSize: 13 }}>
                <FilterOutlined style={{ marginRight: 8, color: COLORS.violet }} />
                Lead-Level Filters
              </span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setLeadFilters({
                    status: [], stage: [], state: [], city: [], timeZone: [], channel: [],
                    assignedTo: [], priority: [], pipelineStage: [], followup: [],
                    successfull: [], callscheduledwithOPs: [], isInPipeline: [],
                    isAssignLocked: [], createdBy: [], notContactedIn: null,
                  });
                  setDateFilters({ lastUpdated: [null, null], lastContacted: [null, null], followUp: [null, null], created: [null, null] });
                  setContactFilters({ title: [], optedIn: [], verified: [], unsubscribed: [] });
                  setSearchText('');
                  setLeadPage(1);
                }}
                style={{
                  background: COLORS.roseBg, border: `1px solid ${COLORS.rose}`, borderRadius: 6,
                  padding: '3px 12px', color: COLORS.rose, fontSize: 11, fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Reset All Filters
              </button>
            </div>
          }
          key="lead-filters"
          style={{ borderBottom: 'none' }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '4px 0' }}>
            {/* Row 1: Core filters */}
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {renderLeadFilterRow('Status', 'status', filterOptions.status || STATUS_OPTIONS)}
              {renderLeadFilterRow('Stage', 'stage', filterOptions.stage || STAGE_OPTIONS)}
              {renderLeadFilterRow('State', 'state', filterOptions.state || [])}
              {renderLeadFilterRow('City', 'city', filterOptions.city || [])}
            </div>
            {/* Row 2 */}
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {renderLeadFilterRow('TimeZone', 'timeZone', filterOptions.timeZone || [])}
              {renderLeadFilterRow('Channel', 'channel', filterOptions.channel || CHANNEL_OPTIONS)}
              {/* Assigned To — with "Not Assigned" option */}
              <div style={filterGroupStyle}>
                <div style={filterLabelStyle}>Assigned To</div>
                <Select
                  mode="multiple"
                  placeholder="Select Assigned To"
                  value={leadFilters.assignedTo}
                  onChange={(val) => setLeadFilters((prev) => ({ ...prev, assignedTo: val }))}
                  style={multiSelectStyle}
                  maxTagCount={2}
                  size="small"
                  allowClear
                  showSearch
                  filterOption={(input, option) => (option?.children || '').toLowerCase().includes(input.toLowerCase())}
                  dropdownStyle={{ background: '#fff' }}
                >
                  <Option key="__not_assigned__" value="__not_assigned__" style={{ color: COLORS.rose, fontWeight: 600 }}>
                    Not Assigned
                  </Option>
                  {(filterOptions.assignedTo || []).map((o) => (
                    <Option key={o} value={o}>{o}</Option>
                  ))}
                </Select>
              </div>
              {renderLeadFilterRow('Priority', 'priority', filterOptions.priority || PRIORITY_OPTIONS)}
            </div>
            {/* Row 3: Pipeline */}
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {renderLeadFilterRow('Pipeline Stage', 'pipelineStage', PIPELINE_STAGE_OPTIONS)}
              {renderLeadFilterRow('Created By', 'createdBy', filterOptions.assignedTo || [])}
              {renderBooleanFilter('Follow Up', 'followup')}
              {renderBooleanFilter('Successful', 'successfull')}
            </div>
            {/* Row 4: Boolean filters */}
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {renderBooleanFilter('Call With OPS', 'callscheduledwithOPs')}
              {renderBooleanFilter('In Pipeline', 'isInPipeline')}
              {renderBooleanFilter('Locked', 'isAssignLocked')}
            </div>
            {/* Date range filters */}
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {renderDateFilter('Last Updated', 'lastUpdated')}
              {renderDateFilter('Last Contacted', 'lastContacted')}
              {renderDateFilter('Follow Up Date', 'followUp')}
              {renderDateFilter('Created Date', 'created')}
            </div>

            {/* Not Contacted In X Days */}
            <div style={{ marginTop: 12 }}>
              <span style={{ ...filterLabelStyle, display: 'block', marginBottom: 4 }}>NOT CONTACTED IN</span>
              <Select
                value={leadFilters.notContactedIn || undefined}
                onChange={(val) => setLeadFilters(prev => ({ ...prev, notContactedIn: val || null }))}
                placeholder="Select days..."
                allowClear
                style={{ width: 200 }}
                dropdownStyle={{ background: '#fff' }}
                options={[
                  { value: 10, label: 'Last 10 days' },
                  { value: 15, label: 'Last 15 days' },
                  { value: 30, label: 'Last 30 days' },
                  { value: 45, label: 'Last 45 days' },
                  { value: 60, label: 'Last 60 days' },
                  { value: 75, label: 'Last 75 days' },
                  { value: 90, label: 'Last 90 days' },
                  { value: 120, label: 'Last 120 days' },
                  { value: 180, label: 'Last 6 months' },
                  { value: 365, label: 'Last 1 year' },
                  { value: 9999, label: 'Never contacted' },
                ]}
              />
            </div>
          </div>
        </Panel>
      </Collapse>

      {/* Contact-Level Filter Bar */}
      <div style={{
        ...glassCard,
        padding: '12px 16px',
        display: 'flex',
        gap: 12,
        alignItems: 'center',
        flexWrap: 'wrap',
      }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: COLORS.textSecondary, marginRight: 4 }}>
          CONTACT FILTERS:
        </span>
        <Select
          mode="multiple"
          placeholder="Title"
          value={contactFilters.title}
          onChange={(val) => setContactFilters((p) => ({ ...p, title: val }))}
          style={{ minWidth: 150 }}
          size="small"
          allowClear
          maxTagCount={1}
          dropdownStyle={{ background: '#fff' }}
        >
          {(() => {
            // Get unique titles from loaded leads
            const titles = new Set();
            filteredLeads.forEach(l => (l.leadsContacts || []).forEach(c => { if (c.title?.trim()) titles.add(c.title.trim()); }));
            // Also add from filterOptions if available
            if (filterOptions?.contactTitle) filterOptions.contactTitle.forEach(t => { if (t?.trim()) titles.add(t.trim()); });
            return [...titles].sort().map(t => <Option key={t} value={t}>{t}</Option>);
          })()}
        </Select>
        <Select
          mode="multiple"
          placeholder="Opted In"
          value={contactFilters.optedIn}
          onChange={(val) => setContactFilters((p) => ({ ...p, optedIn: val }))}
          style={{ minWidth: 120 }}
          size="small"
          allowClear
          maxTagCount={1}
          dropdownStyle={{ background: '#fff' }}
        >
          <Option value="true">Yes</Option>
          <Option value="false">No</Option>
        </Select>
        <Select
          mode="multiple"
          placeholder="Verified"
          value={contactFilters.verified}
          onChange={(val) => setContactFilters((p) => ({ ...p, verified: val }))}
          style={{ minWidth: 120 }}
          size="small"
          allowClear
          maxTagCount={1}
          dropdownStyle={{ background: '#fff' }}
        >
          <Option value="valid">Valid</Option>
          <Option value="invalid">Invalid</Option>
          <Option value="unknown">Unknown</Option>
        </Select>
        <Select
          mode="multiple"
          placeholder="Unsubscribed"
          value={contactFilters.unsubscribed}
          onChange={(val) => setContactFilters((p) => ({ ...p, unsubscribed: val }))}
          style={{ minWidth: 130 }}
          size="small"
          allowClear
          maxTagCount={1}
          dropdownStyle={{ background: '#fff' }}
        >
          <Option value="true">Yes</Option>
          <Option value="false">No</Option>
        </Select>
        <div style={{ flex: 1 }} />
        <Input
          prefix={<SearchOutlined style={{ color: COLORS.textMuted }} />}
          placeholder="Search leads or contacts..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ maxWidth: 260 }}
          size="small"
          allowClear
        />
      </div>

      {/* Expand/Collapse + Select Buttons + Page Summary */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
        <button
          onClick={() => setExpandedLeads(prev => prev === 'all' || (Array.isArray(prev) && prev.length === filteredLeads.length) ? [] : 'all')}
          style={{
            background: '#fff', border: `1px solid ${COLORS.border}`, borderRadius: 8,
            padding: '6px 14px', color: COLORS.textSecondary, fontSize: 12, fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          {expandedLeads === 'all' || (Array.isArray(expandedLeads) && expandedLeads.length === filteredLeads.length) ? '▼ Collapse All' : '▶ Expand All'}
        </button>
      </div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        {/* Select All Matching — server-side, all pages */}
        <button
          disabled={selectingAll}
          onClick={async () => {
            setSelectingAll(true);
            try {
              const result = await dispatch(selectAllMatching({ leadFilters: cleanFilters, contactFilters })).unwrap();
              if (result?.contacts?.length) {
                onContactsChange(result.contacts);
                message.success(`Selected ${result.contacts.length} contacts from ${result.leadsCount} leads`);
              } else {
                message.info('No matching contacts found');
              }
            } catch (err) {
              message.error(typeof err === 'string' ? err : 'Failed to select all matching');
            }
            setSelectingAll(false);
          }}
          style={{
            background: COLORS.violetBg, border: `1px solid ${COLORS.violet}`, borderRadius: 8,
            padding: '6px 16px', color: COLORS.violet, fontSize: 12, fontWeight: 600,
            cursor: selectingAll ? 'wait' : 'pointer', transition: 'all 0.2s',
          }}
        >
          {selectingAll ? 'Selecting...' : 'Select All Matching'}
        </button>
        {/* Select This Page */}
        <button
          onClick={handleSelectAll}
          style={{
            background: COLORS.blueBg, border: `1px solid ${COLORS.blue}`, borderRadius: 8,
            padding: '6px 16px', color: COLORS.blue, fontSize: 12, fontWeight: 600,
            cursor: 'pointer', transition: 'all 0.2s',
          }}
        >
          Select This Page ({allContactKeys.length})
        </button>
        <button
          onClick={handleDeselectAll}
          style={{
            background: COLORS.roseBg, border: `1px solid ${COLORS.rose}`, borderRadius: 8,
            padding: '6px 16px', color: COLORS.rose, fontSize: 12, fontWeight: 600,
            cursor: 'pointer', transition: 'all 0.2s',
          }}
        >
          Deselect All
        </button>
        {selectedContacts.length > 0 && (
          <button
            onClick={() => setShowSelectedOnly((prev) => !prev)}
            style={{
              background: showSelectedOnly ? COLORS.emerald : COLORS.emeraldBg,
              border: `1px solid ${COLORS.emerald}`, borderRadius: 8,
              padding: '6px 16px', color: showSelectedOnly ? '#fff' : COLORS.emerald,
              fontSize: 12, fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s',
            }}
          >
            {showSelectedOnly ? '✕ Hide Selected' : `👁 Show Selected (${selectedContacts.length})`}
          </button>
        )}
        <span style={{ fontSize: 11, color: COLORS.textMuted, marginLeft: 'auto' }}>
          This page: {filteredLeads.length} leads · {allContactKeys.length} contacts | Filtered total: {totalLeadCount} leads · {filteredTotalContacts ?? '...'} contacts
        </span>
      </div>

      {/* Selected Contacts Panel */}
      {showSelectedOnly && selectedContacts.length > 0 && (
        <div style={{ ...glassCard, padding: 0, overflow: 'hidden', maxHeight: 400, overflowY: 'auto', marginBottom: 8 }}>
          <div style={{
            padding: '10px 16px', background: COLORS.emeraldBg,
            borderBottom: `1px solid ${COLORS.border}`,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: COLORS.emerald }}>
              Selected Contacts ({selectedContacts.length})
            </span>
            <button
              onClick={handleDeselectAll}
              style={{
                background: COLORS.roseBg, border: `1px solid ${COLORS.rose}`, borderRadius: 6,
                padding: '3px 12px', color: COLORS.rose, fontSize: 11, fontWeight: 600, cursor: 'pointer',
              }}
            >
              Remove All
            </button>
          </div>
          {(() => {
            // Group selected contacts by lead
            const grouped = {};
            selectedContacts.forEach((sc) => {
              const leadKey = sc.leadId || 'unknown';
              if (!grouped[leadKey]) grouped[leadKey] = { leadName: sc.leadName || sc.agencyName || 'Unknown Lead', contacts: [] };
              grouped[leadKey].contacts.push(sc);
            });
            return Object.entries(grouped).map(([leadId, group]) => (
              <div key={leadId} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                <div style={{ padding: '8px 16px', background: COLORS.surfaceLight, fontWeight: 600, fontSize: 12, color: COLORS.text }}>
                  {group.leadName}
                  <span style={{ marginLeft: 8, fontSize: 11, color: COLORS.textMuted }}>
                    {group.contacts.length} contact{group.contacts.length !== 1 ? 's' : ''}
                  </span>
                </div>
                {group.contacts.map((sc, idx) => (
                  <div
                    key={sc.key || `${sc.leadId}__${sc.email}__${idx}`}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10, padding: '6px 16px 6px 40px',
                      background: 'rgba(59,130,246,0.08)',
                    }}
                  >
                    <Checkbox
                      checked={true}
                      onChange={() => onContactsChange(selectedContacts.filter((c) => c.key !== sc.key))}
                    />
                    <span style={{ fontWeight: 500, color: COLORS.text, fontSize: 12, minWidth: 140 }}>
                      {sc.contactPerson || sc.contactName || 'No Name'}
                    </span>
                    <span style={{ fontSize: 11, color: COLORS.textMuted, minWidth: 80 }}>
                      {sc.title || '-'}
                    </span>
                    <span style={{ fontSize: 11, color: COLORS.textSecondary, flex: 1 }}>
                      {sc.email}
                    </span>
                    <button
                      onClick={() => onContactsChange(selectedContacts.filter((c) => c.key !== sc.key))}
                      style={{
                        background: 'none', border: 'none', color: COLORS.rose,
                        cursor: 'pointer', fontSize: 14, fontWeight: 700, padding: '0 4px',
                      }}
                      title="Remove"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            ));
          })()}
        </div>
      )}

      {/* Results */}
      <div style={{ ...glassCard, padding: 0, overflow: 'hidden', maxHeight: 500, overflowY: 'auto' }}>
        {leadsLoading ? (
          <div style={{ padding: 40, textAlign: 'center' }}>
            <Spin size="large" />
            <div style={{ marginTop: 12, color: COLORS.textSecondary, fontSize: 13 }}>Loading leads...</div>
          </div>
        ) : filteredLeads.length === 0 ? (
          <div style={{ padding: 40 }}>
            <Empty description={<span style={{ color: COLORS.textSecondary }}>No leads match current filters</span>} />
          </div>
        ) : (
          filteredLeads.map((lead) => {
            const contacts = getFilteredContacts(lead.leadsContacts);
            // Hide leads with 0 matching contacts when contact filters are active
            const hasContactFilter = contactFilters.title?.length || contactFilters.optedIn?.length || contactFilters.verified?.length || contactFilters.unsubscribed?.length;
            if (hasContactFilter && contacts.length === 0) return null;

            const isExpanded = expandedLeads === 'all' || expandedLeads.includes(lead.id);
            const leadSelectedCount = contacts.filter((c, idx) =>
              selectedContacts.find((sc) => sc.key === `${lead.id}__${c.email}__${idx}`)
            ).length;

            return (
              <div key={lead.id} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                {/* Lead Header */}
                <div
                  onClick={() => {
                    setExpandedLeads((prev) => {
                      if (prev === 'all') {
                        // Collapse this one — switch from 'all' to explicit list minus this lead
                        return filteredLeads.map(l => l.id).filter(id => id !== lead.id);
                      }
                      return prev.includes(lead.id) ? prev.filter((id) => id !== lead.id) : [...prev, lead.id];
                    });
                  }}
                  style={{
                    padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 12,
                    cursor: 'pointer', background: isExpanded ? COLORS.surfaceLight : 'transparent',
                    transition: 'background 0.2s',
                  }}
                >
                  <CaretRightOutlined
                    style={{
                      color: COLORS.violet, fontSize: 10,
                      transform: isExpanded ? 'rotate(90deg)' : 'rotate(0)',
                      transition: 'transform 0.2s',
                    }}
                  />
                  <span style={{ fontWeight: 600, color: COLORS.text, fontSize: 13, flex: 1 }}>
                    {lead.agencyName || 'Unnamed Lead'}
                  </span>
                  <span style={{ fontSize: 11, color: COLORS.textMuted }}>
                    {lead.city}{lead.city && lead.state ? ', ' : ''}{lead.state}
                  </span>
                  <span style={{
                    padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 600,
                    color: COLORS.violet, background: COLORS.violetBg,
                  }}>
                    {lead.status || 'N/A'}
                  </span>
                  {leadSelectedCount > 0 && (
                    <Badge count={leadSelectedCount} style={{ backgroundColor: COLORS.emerald }} />
                  )}
                  <span style={{ fontSize: 11, color: COLORS.textMuted }}>
                    {contacts.length} contact{contacts.length !== 1 ? 's' : ''}
                  </span>
                </div>

                {/* Contact Rows */}
                {isExpanded && contacts.length > 0 && (
                  <div style={{ padding: '0 16px 8px 40px', display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {contacts.map((contact, idx) => {
                      if (!contact.email) return null;
                      const key = `${lead.id}__${contact.email}__${idx}`;
                      const isSelected = !!selectedContacts.find((sc) => sc.key === key);
                      const isRecent = contact.unsubscribedAt || false;

                      return (
                        <div
                          key={key}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 10, padding: '6px 12px',
                            borderRadius: 8, background: isSelected ? 'rgba(59,130,246,0.08)' : 'transparent',
                            transition: 'background 0.15s', cursor: 'pointer',
                          }}
                          onClick={() => handleContactToggle(lead, contact, idx)}
                        >
                          <Checkbox checked={isSelected} />
                          <span style={{ fontWeight: 500, color: COLORS.text, fontSize: 12, minWidth: 140 }}>
                            {contact.contactPerson || 'No Name'}
                          </span>
                          <span style={{ fontSize: 11, color: COLORS.textMuted, minWidth: 80 }}>
                            {contact.title || '-'}
                          </span>
                          <span style={{ fontSize: 11, color: COLORS.textSecondary, flex: 1 }}>
                            {contact.email}
                          </span>
                          {contact.optIn && contactBadge('Opted In', COLORS.emerald, COLORS.emeraldBg)}
                          {contact.verifyResult === 'valid' && contactBadge('Verified', COLORS.blue, COLORS.blueBg)}
                          {contact.verifyResult === 'invalid' && contactBadge('Invalid', COLORS.rose, COLORS.roseBg)}
                          {isRecent && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, color: COLORS.amber }}>
                              <WarningFilled /> Unsubscribed
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {isExpanded && contacts.length === 0 && (
                  <div style={{ padding: '8px 40px 12px', fontSize: 12, color: COLORS.textMuted }}>
                    No contacts match current contact filters
                  </div>
                )}
              </div>
            );
          })
        )}

        {/* Pagination */}
        {totalLeadCount > 0 && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: 12, padding: '16px 0', borderTop: `1px solid ${COLORS.border}`,
          }}>
            <button
              disabled={leadPage <= 1}
              onClick={() => setLeadPage(p => Math.max(1, p - 1))}
              style={{
                padding: '6px 16px', borderRadius: 6, border: `1px solid ${COLORS.border}`,
                background: leadPage <= 1 ? COLORS.surface : COLORS.violetBg,
                color: leadPage <= 1 ? COLORS.textMuted : COLORS.violet,
                cursor: leadPage <= 1 ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600,
              }}
            >
              Previous
            </button>
            <span style={{ fontSize: 13, color: COLORS.textSecondary }}>
              Page {leadPage} of {totalPages} · {totalLeadCount} total leads
            </span>
            <button
              disabled={leadPage >= totalPages}
              onClick={() => setLeadPage(p => Math.min(totalPages, p + 1))}
              style={{
                padding: '6px 16px', borderRadius: 6, border: `1px solid ${COLORS.border}`,
                background: leadPage >= totalPages ? COLORS.surface : COLORS.violetBg,
                color: leadPage >= totalPages ? COLORS.textMuted : COLORS.violet,
                cursor: leadPage >= totalPages ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600,
              }}
            >
              Next
            </button>
            <select
              value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value)); setLeadPage(1); }}
              style={{
                padding: '6px 10px', borderRadius: 6, border: `1px solid ${COLORS.border}`,
                fontSize: 13, color: COLORS.textSecondary, background: '#fff', cursor: 'pointer',
              }}
            >
              <option value={25}>25 / page</option>
              <option value={50}>50 / page</option>
              <option value={100}>100 / page</option>
              <option value={200}>200 / page</option>
            </select>
          </div>
        )}
      </div>
    </div>
  );
};

export default RecipientSelector;
